import { useEffect, useState } from "react";
import { ArrowRight, ArrowLeft, Sparkles, Compass, Link2, Command, CheckCircle2 } from "lucide-react";

const BRAND = "#5A90F5";

const STEPS = [
  {
    icon: Sparkles,
    eyebrow: "01 · Welcome",
    title: "Welcome to Crescendo.",
    body: "A calendar with a nervous system. Nodes you drop, lines you wire, a plan that runs itself. Let's take a quick five-step spin.",
  },
  {
    icon: Compass,
    eyebrow: "02 · The Sidebar",
    title: "Your workspaces live here.",
    body: "On the left you'll find Workspaces, Classes, Templates, and the Add-Node drawer. Drag rows to reorder, group them into folders, or use Cmd+G to nest.",
  },
  {
    icon: Link2,
    eyebrow: "03 · Drop & wire",
    title: "Drop nodes onto the calendar.",
    body: "Drag a Task, Routine, Trigger, Action, Condition, Sequence, or Note straight onto a day. Pull a line between two nodes to wire them — the graph bends naturally.",
  },
  {
    icon: Command,
    eyebrow: "04 · Move at speed",
    title: "Spotlight · ⌘K",
    body: "Cmd+K (or Ctrl+K) opens Spotlight — fuzzy search over workspaces, classes, templates, events, and actions. Arrows to navigate, Enter to run.",
  },
  {
    icon: CheckCircle2,
    eyebrow: "05 · You're set",
    title: "Time to design your week.",
    body: "Everything auto-saves. Version History rewinds. Reminders show up exactly where the thing they remind you of lives. Dive in.",
  },
];

export default function WelcomeTour({ onClose }) {
  const [i, setI] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const finish = () => {
    setLeaving(true);
    window.setTimeout(() => {
      try { localStorage.removeItem("crescendo:first-run"); } catch {}
      onClose?.();
    }, 260);
  };

  const next = () => { if (i < STEPS.length - 1) setI(i + 1); else finish(); };
  const back = () => { if (i > 0) setI(i - 1); };
  const skip = () => finish();

  const step = STEPS[i];
  const Icon = step.icon;
  const progressPct = ((i + 1) / STEPS.length) * 100;
  const isLast = i === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center" data-testid="welcome-tour">
      <div
        className="absolute inset-0 transition-opacity duration-300"
        style={{
          background: "rgba(0,0,0,0.35)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          opacity: mounted && !leaving ? 1 : 0,
        }}
        onClick={skip}
      />

      <div
        className="relative w-[92vw] max-w-lg rounded-2xl shadow-2xl overflow-hidden"
        style={{
          background: "#F7F7F8",
          border: "1px solid #E4E4E7",
          transition: "transform 300ms cubic-bezier(.2,.8,.2,1), opacity 300ms ease",
          transform: mounted && !leaving ? "translateY(0) scale(1)" : "translateY(14px) scale(0.97)",
          opacity: mounted && !leaving ? 1 : 0,
        }}
      >
        <div className="h-1 w-full bg-[#E4E4E7]">
          <div
            className="h-full transition-all duration-500 ease-out"
            style={{ width: `${progressPct}%`, background: BRAND }}
          />
        </div>

        <div key={i} className="px-8 pt-8 pb-7" data-testid={`welcome-tour-step-${i}`}>
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center border mb-5"
            style={{ background: `${BRAND}14`, borderColor: `${BRAND}40` }}
          >
            <Icon className="w-5 h-5" style={{ color: BRAND }} />
          </div>
          <div className="text-[10px] tracking-[0.18em] uppercase font-mono mb-1.5" style={{ color: BRAND }}>
            {step.eyebrow}
          </div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-[#18181B] mb-3">
            {step.title}
          </h2>
          <p className="text-sm text-[#52525B] leading-relaxed">{step.body}</p>
        </div>

        <div className="px-8 pb-7 flex items-center">
          <button
            onClick={back}
            disabled={i === 0}
            className="flex items-center gap-1.5 text-[13px] text-[#71717A] hover:text-[#18181B] disabled:opacity-0 disabled:pointer-events-none transition-all"
            data-testid="welcome-tour-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>

          <div className="flex-1 flex items-center justify-center gap-1.5">
            {STEPS.map((_, idx) => (
              <span
                key={idx}
                className="rounded-full transition-all duration-300"
                style={{ width: idx === i ? 20 : 5, height: 5, background: idx <= i ? BRAND : "#D4D4D8" }}
              />
            ))}
          </div>

          <div className="flex items-center gap-1">
            {!isLast && (
              <button
                onClick={skip}
                className="px-3 py-2 text-[13px] text-[#A1A1AA] hover:text-[#18181B] transition-colors"
                data-testid="welcome-tour-skip-btn"
              >
                Skip
              </button>
            )}
            <button
              onClick={next}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-[13px] font-medium transition-all hover:opacity-90 active:scale-95"
              style={{ background: BRAND }}
              data-testid="welcome-tour-next"
            >
              {isLast ? "Start Planning" : "Next"}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
